import { ref, onMounted, onUnmounted, type Ref } from 'vue';

export interface UseScrollContainerOptions {
    /**
     * Existing scrollContainer ref to use instead of creating a new one
     */
    scrollContainer?: Ref<HTMLElement | null>;

    /**
     * Custom calculation for scroll amount
     * Default: containerWidth / childCount (or containerWidth / 6 if no children)
     */
    getScrollAmount?: () => number;
}

export function useScrollContainer(options?: UseScrollContainerOptions) {
    const scrollContainer = options?.scrollContainer ?? ref<HTMLElement | null>(null);
    const canScrollLeft = ref(false);
    const canScrollRight = ref(false);
    const isScrolling = ref(false);

    const getDefaultScrollAmount = (): number => {
        if (!scrollContainer.value) return 0;

        const containerWidth = scrollContainer.value.clientWidth;
        const childCount = scrollContainer.value.children.length;

        if (childCount === 0) {
            return containerWidth / 6;
        }

        return containerWidth / childCount;
    };

    const getScrollAmount = options?.getScrollAmount || getDefaultScrollAmount;

    const updateScrollState = () => {
        if (!scrollContainer.value) return;

        const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;

        canScrollLeft.value = scrollLeft > 0;
        canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 1;
    };

    const scrollLeft = () => {
        if (scrollContainer.value && canScrollLeft.value) {
            isScrolling.value = true;
            scrollContainer.value.scrollBy({
                left: -getScrollAmount(),
                behavior: 'smooth',
            });
            setTimeout(() => {
                isScrolling.value = false;
            }, 500);
        }
    };

    const scrollRight = () => {
        if (scrollContainer.value && canScrollRight.value) {
            isScrolling.value = true;
            scrollContainer.value.scrollBy({
                left: getScrollAmount(),
                behavior: 'smooth',
            });
            setTimeout(() => {
                isScrolling.value = false;
            }, 500);
        }
    };

    onMounted(() => {
        updateScrollState();
        window.addEventListener('resize', updateScrollState);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', updateScrollState);
    });

    return {
        scrollContainer,
        canScrollLeft,
        canScrollRight,
        isScrolling,
        scrollLeft,
        scrollRight,
        updateScrollState,
    };
}
